/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Components
import Badge from 'shared/ui/components/Badge.vue';
import ExpandableContent from 'shared/ui/components/ExpandableContent.vue';

// Utils
import { diff } from 'semver';
import { isObject } from 'lodash';
import { chain, uniq } from 'underscore';

// view helper

/**
 * extract package name from conflict structure:
 * { packageA: { packageB: '^1.0.0'}, packageC: { packageB: '^1.0.0'} } => 'packageB'
 *
 * @param {Object} conflicts
 * @param {Object} packageNamesToTitlesMap
 * @return {Array}
 *
 * @private
 */
const getConflictingPackages = (conflicts, packageNamesToTitlesMap) => chain(conflicts)
    .keys()
    .map(sourcePackage => Object.keys(conflicts[sourcePackage]))
    .flatten()
    .uniq()
    .map(packageName => packageNamesToTitlesMap[packageName] || packageName)
    .value();

/**
 * This component is used to show a wizard to install or update a package.
 *
 * @example
 * PackageWizard(:module="module" :eventId="eventId" :orgId="orgId")
 *
 * @vue-prop {Object} module the package details
 * @vue-prop {String} eventId the ID of the event
 * @vue-prop {String} orgId the ID of the organization
 * @vue-prop {'single'|'marketplace'|'libs'|'critical'|'all'} type=single the type of update (default single)
 * @vue-prop {String} [classes='']
 * @vue-prop {Boolean} [remove=false]
 * @vue-prop {String} [context='']
 *
 * @category ui.components
 * @exports PackageWizard
 * @component
 */
const __vue_script__ = {
    name: 'PackageWizard',

    components: {
        Badge, ExpandableContent
    },

    props: {
        module: {
            type: Object,
            default: null
        },
        eventId: {
            type: String,
            required: true
        },
        orgId: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'single'
        },
        classes: {
            type: String,
            default: ''
        },
        remove: {
            type: Boolean,
            default: false
        },
        context: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            action: 'install',
            dependencies: [],
            error: null,
            conflicts: null,
            installed: false,
            jobResultsPath: '',
            majorUpdate: false,
            pkg: {},
            progress: 0,
            updateProgress: progress => this.progress = progress,
            showPaidAlert: false,
            paidWarningMessage: '',
            step: 0,
            tooltip: undefined,
            updated: false,
            updates: [],
            showOnInstallRoutes: [],
            librariesToUpdate: []
        };
    },

    computed: {
        /**
         * This computed property is used to determine if we need to show a disclaimer
         * or not during the installation process.
         *
         * @returns {Boolean} whether to show disclaimer or not
         */
        showInstallDisclaimer() {
            if (this.action === 'update_all') {
                return false;
            } else if (this.action === 'update') {
                return this.dependencies.length > 0 || this.updates.length > 1;
            } else {
                return this.dependencies.length > 0 || this.updates.length > 0;
            }
        }
    },

    watch: {
        step() {
            if (this.step === 10) {
                this.installed = this.action === 'install';
                this.updated = this.action === 'update';
            }
        }
    },

    created() {
        if (this.module) {
            this.pkg = this.module;
        } else {
            // If no module have been provided then we are going to update all packages
            this.action = 'update_all';
        }

        if (this.pkg.update) {
            this.action = 'update';
            this.majorUpdate = diff(this.pkg.installedVersion, this.pkg.latest) === 'major';
            if (this.majorUpdate) {
                console.info('[PackageWizard] Major update detected %s: %s -> %s', this.pkg.name, this.pkg.installedVersion, this.pkg.latest);
            }
        }

        if (this.remove) {
            this.action = 'uninstall';
        }

        this.installed = this.pkg.installed;
        this.jobResultsPath = this.$services.market.getQueueJobResultUrl(this.eventId);
        this.showPaidAlert = !this.pkg.update && this.action !== 'uninstall' && (this.pkg.paid || this.pkg.paid_add_on);
        const canForceInstallPaidPkg = this.$services.user.hasPermission('pkg_repo_realms.force_install_paid_package');

        if (canForceInstallPaidPkg) {
            this.paidWarningMessage = this.$i18n.t('marketplace.package.paid_alert_support');
        } else if (this.pkg.paid_add_on) {
            this.paidWarningMessage = this.$i18n.t('marketplace.package.paid_alert_add_on');
        } else {
            this.paidWarningMessage = this.$i18n.t('marketplace.package.paid_alert');
        }

        this.dryRun();
    },

    methods: {
        /**
         * Simulates the package install or update and checks for dependencies and other updates.
         */
        async dryRun() {
            const jobId = await this.registerJob(true);

            if (!jobId) {
                // Something went wrong, stop here.
                console.error('[PackageWizard] Can\'t register the `dryrun` job.');
                return;
            }

            try {
                const jobRes = await this.$services.jobs.waitFor({
                    jobId,
                    resultPath: this.jobResultsPath,
                    updateUIProgress: this.updateProgress
                });

                this.confirm(jobRes);
            } catch (error) {
                if (isObject(error) && error.status === 'error') {
                    this.setError(error.error, error.conflicts);
                } else {
                    this.setError(error);
                }
            }
        },

        /**
         * Displays the install or update confirmation.
         *
         * @param {Object} jobResult result of the job
         */
        async confirm(jobResult) {
            // `error` will be present in any case if an error occurs during the request.
            const { data, status, error, conflicts } = jobResult;

            if (status === 'error') {
                this.setError(error, conflicts);
                return;
            }

            const { install, uninstall, update, libs } = this.composeResults(data);

            this.step = 1;
            this.dependencies = this.action === 'uninstall' ? uninstall : install;
            this.updates = update;
            this.librariesToUpdate = libs;

            console.info('[PackageWizard] Dry run results:', this.dependencies, this.updates.length ? this.updates : this.librariesToUpdate);
        },

        /**
         * Builds the updated and installed packages collection.
         * NOTE: this method also checks for major packages updates and marks them.
         *
         * @private
         *
         * @param {Object} data the collection of impacted packages.
         *
         * @return {Object} an array of filtered impacted packages.
         */
        composeResults(data) {
            // `installed` and `updated` keys are used in case of install or update actions.
            // `updates` is used in case of `updateAll`.
            // `uninstalled` is used in case of `uninstall`.
            const { installed, uninstalled, updated, updates } = data;
            const results = updates || { pkg: { updated, installed, uninstalled } };

            const installedPackages = [];
            const uninstalledPackages = [];
            const updatedPackages = [];

            for (const pkgName of Object.keys(results)) {
                const pkg = results[pkgName];

                pkg.uninstalled = pkg.uninstalled || [];

                installedPackages.push(...pkg.installed);
                uninstalledPackages.push(...pkg.uninstalled);

                // Check for major updates (only for packages on the marketplace)
                for (const up of pkg.updated) {
                    if (up.title && diff(up.previous_version, up.current_version) === 'major') {
                        console.info('[PackageWizard] Major update detected:', up);
                        up.major = true;
                    }
                    updatedPackages.push(up);
                }
            }

            // If acting on the modules, filter out this package and no-marketplace packages from the results.
            // `no-marketplace` packages are those that have not the `title` property set.
            // For `update` we still want to show the `What's new` section for the currently updated pkg
            const filterFn = pkgs => {
                const filtered = pkgs.filter(pkg => {
                    const condition = pkg.title;

                    return condition && (this.action === 'update' || pkg.name !== this.pkg.name);
                });

                return uniq(filtered, pkg => pkg.name);
            };

            return {
                install: filterFn(installedPackages),
                uninstall: filterFn(uninstalledPackages),
                update: filterFn(updatedPackages),
                libs: updatedPackages.filter(pkg => !pkg.title)
            };
        },

        /**
         * Performs actual package action.
         */
        async performAction() {
            let modulesBeforeInstall;

            this.step = 2;
            this.resetProgress();

            if (this.action === 'install') {
                modulesBeforeInstall = this.$services.blueprint.getModules();
            }

            const jobId = await this.registerJob();

            if (!jobId) {
                // Something went wrong, stop here.
                console.error('[PackageWizard] Can\'t register the `%s` job.', this.action);
                return;
            }

            try {
                await this.$services.jobs.waitFor({
                    jobId,
                    resultPath: this.jobResultsPath,
                    updateUIProgress: this.updateProgress
                });
                this.trackAction();
            } catch ({ status, error, conflicts }) {
                return this.setError(error, conflicts);
            }

            if (this.action === 'install') {
                try {
                    const user = this.$services.user.getCurrentUser();
                    const event = await this.$services.event.getEventCached(this.eventId);

                    await this.$services.blueprint.loadBlueprint({ event, user, require: BSTG.require });
                    const showOnInstallCtrls = this.$services.blueprint.getShowOnInstallCtrls(modulesBeforeInstall);

                    this.showOnInstallRoutes = showOnInstallCtrls.map(ctrl => ({
                        title: ctrl.title,
                        route: this.$services.blueprint.getRouteForModule(ctrl)
                    }));
                } catch (error) {
                    return this.setError(error);
                }
            }

            this.step = 10;
        },

        /**
         * Sets the error and displays it to the user.
         *
         * @param {String} error the eventual occurred error.
         * @param {Object} [conflicts] optional conflicts
         */
        async setError(error, conflicts) {
            let message = [];

            if (typeof error === 'string') {
                message.push(error);
                // extract list of packages that should be updated first
                if (conflicts) {
                    const packageNamesToTitlesMap = await this.getPackageNamesToTitlesMap();

                    this.conflicts = getConflictingPackages(conflicts, packageNamesToTitlesMap);
                }
            } else if (isObject(error)) {
                for (const pkgName of Object.keys(error)) {
                    message.push(`${pkgName}: ${error[pkgName]}`);
                }
                // extract list of packages that should be updated first.
                // here the structure has one extra level
                if (conflicts) {
                    const packageNamesToTitlesMap = await this.getPackageNamesToTitlesMap();

                    this.conflicts = chain(conflicts)
                        .keys()
                        .map(installedPackage =>
                            getConflictingPackages(conflicts[installedPackage], packageNamesToTitlesMap)
                        )
                        .flatten()
                        .uniq()
                        .value();
                }
            } else {
                message = error || [];
            }

            this.error = message;
            this.step = -1;

            this.trackAction();
        },

        /**
         * Remaps package name with their respective "human readable" version.
         *
         * @returns {Promise<Object>} a promise of an object with package name as a key and its title as
         */
        async getPackageNamesToTitlesMap() {
            const packages = await this.$services.market.getMarketplaceDescriptions(this.eventId);

            return packages.reduce((acc, marketplacePackage) => {
                acc[marketplacePackage.name] = marketplacePackage.title;
                return acc;
            }, {});
        },

        /**
         * Resets the component to its initial state.
         */
        reset() {
            this.resetProgress();
            this.step = 0;
            this.error = null;
            this.conflicts = null;
            this.$emit('close');
        },

        /**
         * Resets the progress
         */
        resetProgress() {
            this.progress = 0;
        },

        /**
         * Since a package could add new features to backstage,
         * we reload the whole page.
         */
        reload() {
            window.location.reload();
        },

        /**
         * Force redirects to given route
         *
         * @param {String} route
         */
        forceRedirect(route) {
            this.$router.forcePush(route);
        },

        /**
         * Stores the install or update job on the backend job runner, if something goes wrong
         * during this process shows an error and exits.
         *
         * @param {Boolean} [dryrun=false] whether to simulate the install or run the actual installation
         * @param {String} [version=null] optional specific version of the package
         *
         * @returns {Promise<Number>|undefined} the job ID or undefined
         */
        async registerJob(dryrun = false, version = null) {
            const { name, latest, patch } = this.pkg;
            let response = {};

            if (this.type === 'critical') {
                version = patch;
            } else {
                version = latest;
            }

            try {
                switch (this.action) {
                    case 'install':
                        response = await this.$services.market.installPackage(this.eventId, name, version, dryrun);
                        break;
                    case 'uninstall':
                        response = await this.$services.market.uninstallPackage(this.eventId, name, dryrun);
                        break;
                    case 'update':
                        response = await this.$services.market.updatePackage(this.eventId, name, version, dryrun);
                        break;
                    case 'update_all':
                        response = await this.$services.market.updateAllPackages(this.eventId, this.type, dryrun);
                        break;
                }

                const { jobId, error, message } = response;

                if (error) {
                    let msg = this.$i18n.t(error).toString();

                    if (typeof message === 'object' && message.error) {
                        msg = message.error;
                    }

                    this.setError(msg);
                } else {
                    return jobId;
                }

            } catch (error) {
                this.setError(error);
            }
        },

        /**
         * Reports execution outcome to the analytics.
         */
        trackAction() {
            const trackingPayload = {
                eid: this.eventId,
                context: this.context
            };

            if (this.module) {
                trackingPayload.package = this.pkg.name;
            }

            if (this.error) {
                trackingPayload.error = this.error;
            }

            this.$track(`package_${this.action}`, trackingPayload);
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"pkg-upgrade"},[(_vm.step === 0)?_c('div',{staticClass:"step modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_c('h3',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".preparing"))))])]),_c('div',{staticClass:"modal-body"},[_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".package_note"))))]),_c('progress-bar',{attrs:{"progress":_vm.progress}})],1),_c('div',{staticClass:"modal-footer"},[_c('footer',{staticClass:"actions",attrs:{"slot":"bottom"},slot:"bottom"},[_c('button',{staticClass:"btn tertiary",on:{"click":function($event){return _vm.reset()}}},[_vm._v(_vm._s(_vm.$t('general.cancel')))])])])]):_vm._e(),(_vm.step === 1)?_c('div',{staticClass:"step modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_c('h3',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".confirm_title"))))])]),_c('div',{staticClass:"modal-body"},[(_vm.majorUpdate)?_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".major"))))]):_vm._e(),(_vm.showPaidAlert)?_c('p',{domProps:{"innerHTML":_vm._s(_vm.paidWarningMessage)}}):_vm._e(),(_vm.dependencies.length || _vm.updates.length || _vm.librariesToUpdate.length)?_c('section',{staticClass:"packages-changes"},[(_vm.showInstallDisclaimer)?_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".dependencies"))))]):_vm._e(),_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".continue"), [_vm.pkg.title || _vm.pkg.name])))]),_vm._l((_vm.dependencies),function(dependency){return _c('div',{staticClass:"list-item installs"},[_c('expandable-content',{attrs:{"togglerText":"marketplace.see_more"}},[_c('div',{attrs:{"slot":"header"},slot:"header"},[_c('strong',[_vm._v(_vm._s(dependency.title || dependency.name))]),(_vm.action !== 'uninstall')?_c('Badge',{staticClass:"success"},[_vm._v(_vm._s(_vm.$t("marketplace.package.install")))]):_vm._e(),_c('span',{staticClass:"version"},[_vm._v(_vm._s(dependency.version || dependency.current_version))])],1),(dependency.tagline)?_c('div',{staticClass:"desc",domProps:{"innerHTML":_vm._s(dependency.tagline)}}):_vm._e()])],1)}),(_vm.updates.length)?_c('h5',[_vm._v(_vm._s(_vm.$t('marketplace.package.modules')))]):_vm._e(),_c('div',{staticClass:"updatables-list"},_vm._l((_vm.updates),function(update){return _c('div',{staticClass:"list-item updates",class:[update.major ? 'highlight' : '']},[_c('expandable-content',{attrs:{"toggler-text":"marketplace.package.changelog","content-class":"changelog","show-toggler":update.changelog !== undefined}},[_c('div',{attrs:{"slot":"header"},slot:"header"},[_c('strong',[_vm._v(_vm._s(update.title || update.name))]),_c('Badge',{staticClass:"info"},[_vm._v(_vm._s(_vm.$t("marketplace.package.update")))]),_c('span',{staticClass:"version"},[_vm._v(_vm._s(update.previous_version)+" → "+_vm._s(update.current_version))]),(update.major)?_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".major"))))]):_vm._e()],1),(update.changelog)?_c('div',{staticClass:"log",domProps:{"innerHTML":_vm._s(update.changelog.changelog)}}):_vm._e()])],1)}),0),(_vm.librariesToUpdate.length)?_c('h5',[_vm._v(_vm._s(_vm.$t('marketplace.package.libraries')))]):_vm._e(),_c('div',{staticClass:"updatables-list"},_vm._l((_vm.librariesToUpdate),function(update){return _c('div',{staticClass:"list-item updates",class:[update.major ? 'highlight' : '']},[_c('expandable-content',{attrs:{"toggler-text":"marketplace.package.changelog","content-class":"changelog","show-toggler":update.changelog !== undefined}},[_c('div',{attrs:{"slot":"header"},slot:"header"},[_c('strong',[_vm._v(_vm._s(update.title || update.name))]),_c('Badge',{staticClass:"info"},[_vm._v(_vm._s(_vm.$t("marketplace.package.update")))]),_c('span',{staticClass:"version"},[_vm._v(_vm._s(update.previous_version)+" → "+_vm._s(update.current_version))]),(update.major)?_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".major"))))]):_vm._e()],1),(update.changelog)?_c('div',{staticClass:"log",domProps:{"innerHTML":_vm._s(update.changelog.changelog)}}):_vm._e()])],1)}),0)],2):_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".continue"), [_vm.pkg.title || _vm.pkg.name])))])]),_c('div',{staticClass:"modal-footer"},[_c('footer',{staticClass:"actions",attrs:{"slot":"bottom"},slot:"bottom"},[_c('button',{staticClass:"btn tertiary",on:{"click":function($event){return _vm.reset()}}},[_vm._v(_vm._s(_vm.$t('general.cancel')))]),_c('button',{staticClass:"btn primary",on:{"click":function($event){return _vm.performAction()}}},[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".confirm"))))])])])]):_vm._e(),(_vm.step === 2)?_c('div',{staticClass:"step modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_c('h3',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".performing"))))])]),_c('div',{staticClass:"modal-body"},[_c('p',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".package_note"))))])]),_c('div',{staticClass:"modal-footer"},[_c('progress-bar',{attrs:{"progress":_vm.progress}})],1)]):_vm._e(),(_vm.step === 10)?_c('div',{staticClass:"step modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_c('h3',[_vm._v(_vm._s(_vm.$t(("marketplace." + _vm.action + ".completed"))))])]),_c('div',{staticClass:"modal-body"},[_c('p',[_vm._v(_vm._s(_vm.$t('marketplace.need_refresh')))])]),_c('div',{staticClass:"modal-footer"},[_c('footer',{staticClass:"actions",attrs:{"slot":"bottom"},slot:"bottom"},[_vm._l((_vm.showOnInstallRoutes),function(link){return (_vm.action === 'install')?_c('button',{staticClass:"btn btn-default",on:{"click":function($event){return _vm.forceRedirect(link.route)}}},[_vm._v(_vm._s(link.title))]):_vm._e()}),_c('button',{staticClass:"btn primary",on:{"click":function($event){return _vm.reload()}}},[_vm._v(_vm._s(_vm.$t('general.done')))])],2)])]):_vm._e(),(_vm.error)?_c('div',{staticClass:"errors modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_c('h3',[_vm._v(_vm._s(_vm.$t('general.something_wrong')))])]),_c('div',{staticClass:"modal-body"},[(_vm.error.length !== 0)?_c('p',[_vm._v(_vm._s(_vm.$t('general.error_occurred'))+":")]):_vm._e(),_vm._l((_vm.error),function(err){return _c('p',[_vm._v(_vm._s(err))])}),(_vm.conflicts)?_c('p',[_vm._v(_vm._s(_vm.$t('general.conflicting_packages')))]):_vm._e(),(_vm.conflicts)?_c('ul',_vm._l((_vm.conflicts),function(conflict){return _c('li',[_vm._v(_vm._s(conflict))])}),0):_vm._e(),_c('p',[_vm._v(_vm._s(_vm.$t('general.try_later')))])],2),_c('div',{staticClass:"modal-footer"},[_c('footer',{staticClass:"actions",attrs:{"slot":"bottom"},slot:"bottom"},[_c('button',{staticClass:"btn primary",on:{"click":function($event){return _vm.reset()}}},[_vm._v(_vm._s(_vm.$t('general.close')))])])])]):_vm._e()])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "PackageWizard.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__